<template>
  <v-app :dark="isDarkMode" :class="['about-page-bg', { 'dark-mode': isDarkMode }]">

    <!-- Header -->
    <header class="autosyncx-header">
      <!-- Main Navigation Bar -->
      <div class="header-nav">
        <div class="header-nav-content">

          <!-- Mobile: Hamburger -->
          <div class="hamburger-menu" @click="toggleMobileMenu">
            <v-icon class="hamburger-icon material-icons-round">{{ mobileMenuOpen ? 'close' : 'menu' }}</v-icon>
          </div>

          <!-- Desktop: Nav Links -->
          <nav class="nav-tabs">
            <div class="nav-tab" @click="$router.push('/')">HOME</div>

            <v-menu offset-y open-on-click content-class="cars-dropdown-menu">
              <template v-slot:activator="{ on, attrs }">
                <div class="nav-tab nav-tab-dropdown" v-bind="attrs" v-on="on">
                  &#9660;&nbsp;CARS FOR SALE
                </div>
              </template>
              <v-list dense class="cars-dropdown-list">
                <v-list-item @click="$router.push('/')">
                  <v-list-item-title>All Vehicles</v-list-item-title>
                </v-list-item>
                <v-list-item @click="$router.push('/')">
                  <v-list-item-title>Available</v-list-item-title>
                </v-list-item>
                <v-list-item @click="$router.push('/')">
                  <v-list-item-title>Pending</v-list-item-title>
                </v-list-item>
                <v-list-item @click="$router.push('/')">
                  <v-list-item-title>Sold</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- <div class="nav-tab" @click="$router.push('/reviews')">TESTIMONIALS</div> -->
            <!-- <div class="nav-tab">FINANCE</div> -->
            <div class="nav-tab active" @click="$router.push('/about-us')">ABOUT US</div>
            <!-- <div class="nav-tab">CONTACT</div> -->
          </nav>

          <!-- Right: Theme Toggle -->
          <div class="nav-right">
            <div class="theme-toggle">
              <v-btn icon small @click="toggleDarkMode" class="theme-toggle-btn">
                <v-icon small class="material-icons-round">{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Mobile Menu Dropdown -->
        <v-expand-transition>
          <div v-if="mobileMenuOpen" class="mobile-menu-dropdown">
            <div class="mobile-menu-item" @click="$router.push('/'); mobileMenuOpen = false">
              <i class="material-icons-round menu-item-icon">home</i>
              <span>Home</span>
            </div>
            <div class="mobile-menu-item" @click="$router.push('/'); mobileMenuOpen = false">
              <i class="material-icons-round menu-item-icon">directions_car</i>
              <span>Cars For Sale</span>
            </div>
            <div class="mobile-menu-item" @click="$router.push('/reviews'); mobileMenuOpen = false">
              <i class="material-icons-round menu-item-icon">star</i>
              <span>Testimonials</span>
            </div>
            <div class="mobile-menu-item active-item" @click="mobileMenuOpen = false">
              <i class="material-icons-round menu-item-icon">info</i>
              <span>About Us</span>
            </div>
            <div class="mobile-menu-item" @click="toggleDarkMode">
              <i class="material-icons-round menu-item-icon">{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</i>
              <span>{{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}</span>
            </div>
          </div>
        </v-expand-transition>
      </div>
    </header>

    <!-- Hero -->
    <div class="about-hero">
      <div class="hero-content">
        <h1 class="hero-title">About Us</h1>
        <p class="hero-subtitle">Herrera Luxury Auto Sales</p>
        <p class="hero-tagline">Built on trust. Driven by relationships.</p>
      </div>
    </div>

    <!-- Team Section -->
    <div class="team-section">
      <v-container>
        <h2 class="section-title">Meet the Team</h2>

        <v-row>
          <!-- Jay Herrera -->
          <v-col cols="12" md="6" class="mb-6">
            <div class="team-card founder-card">
              <div class="member-avatar founder-avatar">
                <img src="@/assets/img/avatar-jay.png" alt="Jay Herrera" />
              </div>
              <div class="member-info">
                <div class="member-badge founder-badge">Founder</div>
                <h3 class="member-name">Jay</h3>
                <p class="member-title">Founder &amp; Face of Herrera Luxury Auto Sales</p>
                <p class="member-bio">
                  Growing up in the Goodyear/Phoenix area, I developed an early passion for business, quality,
                  and the belief that the people around you deserve the best. That mindset led me to entrepreneurship
                  at a young age and eventually to building Herrera Luxury Auto Sales from the ground up.
                </p>
                <p class="member-bio">
                  My mission is simple: make the luxury car-buying experience feel personal, transparent, and
                  genuinely exciting. Whether you're looking for a refined family vehicle or your next statement car,
                  I want every client who walks through our doors to feel taken care of by someone who actually cares —
                  not just a salesperson chasing a commission.
                </p>
                <p class="member-quote">"Come see us. Let's find you something worth driving." — Jay</p>
              </div>
            </div>
          </v-col>

          <!-- Mark A. Williams Sr. -->
          <v-col cols="12" md="6" class="mb-6">
            <div class="team-card">
              <div class="member-avatar">
                <img src="@/assets/img/avatar-mark.png" alt="Mark A. Williams Sr." />
              </div>
              <div class="member-info">
                <div class="member-badge co-founder-badge">Co-Founder</div>
                <h3 class="member-name">Mark</h3>
                <p class="member-title">Operations Manager / General Manager</p>
                <p class="member-bio">
                  With over 25 years of experience in customer service, operations, and financial management, Mark brings
                  strong leadership and operational expertise to the dealership. His background includes vehicle sourcing,
                  deal structuring, title and DMV processing, and inventory and financial oversight.
                </p>
                <p class="member-bio">
                  Known for his integrity, attention to detail, and ability to build strong teams, Mark leads daily
                  operations with a focus on efficiency, compliance, and delivering a seamless customer experience.
                </p>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6" class="mb-6">
            <div class="team-card">
              <div class="member-avatar">
                <img src="@/assets/img/avatar-david.png" alt="David" />
              </div>
              <div class="member-info">
                <div class="member-badge co-founder-badge">Co-Founder &amp; CTO</div>
                <h3 class="member-name">David</h3>
                <p class="member-title">Bridge Builder</p>
                <p class="member-bio">
                  One of five Co-Founders and CTO of Herrera Luxury Auto Sales — but his real title is Bridge Builder.
                  With over 18 years in the automotive industry, David brings a depth of experience that few can match.
                </p>
                <p class="member-bio">
                  He believes every great deal starts with a relationship, and his role at Herrera is simple: connect
                  the right people, build the right systems, and make sure every client feels known from the first
                  conversation to the final handshake.
                </p>
              </div>
            </div>
          </v-col>
          

          <!-- Stephanie -->
          <v-col cols="12" md="6" class="mb-6">
            <div class="team-card">
              <div class="member-avatar">
                <img src="@/assets/img/avatar-step.png" alt="Stephanie" />
              </div>
              <div class="member-info">
                <div class="member-badge co-founder-badge">Co-Founder</div>
                <h3 class="member-name">Stephanie</h3>
                <p class="member-title">Business Formation &amp; Compliance Lead</p>
                <p class="member-bio">
                  With 19 years of legal experience, a paralegal degree, and certification by the Arizona Supreme Court,
                  she brings a strong foundation in regulatory compliance, legal structure, and operational integrity.
                </p>
                <p class="member-bio">
                  Stephanie is a sharp analytical thinker, deeply committed to ethical practices and informed
                  decision-making — ensuring that both the team and every client interaction reflect the highest
                  standards of professionalism. At Herrera, she leads with a clear focus on doing what is right,
                  building trust, maintaining transparency, and ensuring every customer experience is handled with
                  integrity from start to finish.
                </p>
              </div>
            </div>
          </v-col>
          <!-- David -->
          <!-- Jerome V. Green -->
          <v-col cols="12" md="6" class="mb-6">
            <div class="team-card">
              <div class="member-avatar">
                <img src="@/assets/img/avatar-mark.png" alt="Jerome V. Green" />
              </div>
              <div class="member-info">
                <h3 class="member-name">Jerome</h3>
                <p class="member-title">Vehicle Processing, Sales Execution &amp; Lot Operations Manager</p>
                <p class="member-bio">
                  With over 30 years of experience in sales and customer service, Jerome is dedicated to delivering
                  a transparent, customer-focused buying experience. He is known for his ability to truly listen,
                  ensuring every client is matched with the right vehicle — never pressured into decisions that
                  don't align with their needs or budget.
                </p>
                <p class="member-bio">
                  Jerome believes that a successful deal begins with trust and continues through lasting relationships.
                  His commitment to service extends beyond the sale, creating an experience that clients feel confident
                  recommending to friends and family.
                </p>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- CTA -->
        <div class="about-cta">
          <p class="cta-text">Ready to find your perfect vehicle?</p>
          <router-link to="/view-inventory">
            <v-btn class="cta-btn" large>View Our Inventory</v-btn>
          </router-link>
        </div>

      </v-container>
    </div>

  </v-app>
</template>

<script>
export default {
  name: 'AboutUs',
  data() {
    return {
      isDarkMode: localStorage.getItem('herrera_theme') !== 'light',
      mobileMenuOpen: false,
    };
  },
  methods: {
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      localStorage.setItem('herrera_theme', this.isDarkMode ? 'dark' : 'light');
      this.mobileMenuOpen = false;
    },
  },
};
</script>

<style>
/* Remove Vuetify default blue/grey background */

</style>

<style scoped>
.about-page-bg {
  background-image: url('~@/assets/img/bg-inventory.jpeg')!important;
  background-size: cover!important;
  background-position: center top!important;
  background-attachment: fixed!important;
  background-repeat: no-repeat!important;
  min-height: 100vh!important;
}

/* =============================================
   Header
   ============================================= */
.autosyncx-header {
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-nav {
  background: #ffffff;
  padding: 0 30px;
  min-height: 64px;
  border-bottom: 2px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  position: relative;
}

.header-nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 64px;
}

.nav-tabs {
  display: flex;
  align-items: center;
}

.nav-tab {
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 18px;
  color: #1a202c;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
  white-space: nowrap;
  user-select: none;
}

.nav-tab:hover {
  color: #3d4a5c;
  border-bottom-color: #3d4a5c;
  background: rgba(61, 74, 92, 0.05);
}

.nav-tab.active {
  color: #3d4a5c;
  border-bottom-color: #3d4a5c;
  background: rgba(61, 74, 92, 0.07);
}

.nav-tab-dropdown {
  position: relative;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.theme-toggle-btn {
  color: #718096 !important;
  transition: all 0.3s ease;
}

.theme-toggle-btn:hover {
  color: #3d4a5c !important;
  background: rgba(61, 74, 92, 0.08) !important;
}

/* Hamburger */
.hamburger-menu {
  display: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.hamburger-menu:hover {
  background: rgba(0, 0, 0, 0.06);
}

.hamburger-icon {
  color: #2d3748 !important;
  font-size: 28px !important;
}

/* Ticker Banner */
.header-ticker {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 9px 30px;
  text-align: center;
}

.ticker-text {
  font-size: 0.875rem;
  color: #4a5568;
  font-style: italic;
  letter-spacing: 0.2px;
}

/* Cars For Sale Dropdown */
.cars-dropdown-menu {
  border-radius: 6px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12) !important;
}

.cars-dropdown-list .v-list-item {
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  color: #1a202c !important;
}

.cars-dropdown-list .v-list-item:hover {
  background: rgba(61, 74, 92, 0.07) !important;
}

/* Mobile Menu Dropdown */
.mobile-menu-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  padding: 10px 0;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  color: #374151;
  cursor: pointer;
  transition: all 0.3s ease;
}

.mobile-menu-item:hover,
.mobile-menu-item.active-item {
  background: rgba(61, 74, 92, 0.08);
  color: #3d4a5c;
}

.menu-item-icon {
  color: #3d4a5c !important;
  font-size: 22px !important;
}

/* Dark Mode Header */
.dark-mode .header-nav {
  background: #1a1230 !important;
  border-bottom: 1px solid rgba(255,255,255,0.1) !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.4) !important;
}

.dark-mode .header-ticker {
  background: #1a1230 !important;
  border-bottom: 1px solid rgba(255,255,255,0.1) !important;
}

.dark-mode .ticker-text {
  color: #a0aec0 !important;
}

.dark-mode .nav-tab {
  color: #e2e8f0 !important;
}

.dark-mode .nav-tab:hover,
.dark-mode .nav-tab.active {
  color: #ffffff !important;
  border-bottom-color: #7c5cff !important;
  background: rgba(124, 92, 255, 0.1) !important;
}

.dark-mode .hamburger-icon {
  color: #ffffff !important;
}

.dark-mode .theme-toggle-btn {
  color: #fbbf24 !important;
}

.dark-mode .mobile-menu-dropdown {
  background: rgba(26, 18, 48, 0.98) !important;
}

.dark-mode .mobile-menu-item {
  color: #ffffff !important;
}

.dark-mode .mobile-menu-item:hover,
.dark-mode .mobile-menu-item.active-item {
  background: rgba(124, 92, 255, 0.15) !important;
}

.dark-mode .menu-item-icon {
  color: #a78bfa !important;
}

.dark-mode .cars-dropdown-list .v-list-item {
  color: #ffffff !important;
}

/* Responsive */
@media (max-width: 768px) {
  .header-nav {
    padding: 0 15px;
    position: relative;
  }
  .nav-tabs { display: none; }
  .hamburger-menu {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* Hero */
.about-hero {
  padding: 48px 24px 48px;
  text-align: center;
}
.hero-title {
  font-size: 3rem;
  font-weight: 800;
  color: #fff;
  text-shadow: 0 2px 12px rgba(0,0,0,0.5);
  margin-bottom: 8px;
}
.hero-subtitle {
  font-size: 1.4rem;
  font-weight: 600;
  color: #f1f5f9;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);
  margin-bottom: 8px;
}
.hero-tagline {
  font-size: 1.1rem;
  color: #e2e8f0;
  text-shadow: 0 1px 6px rgba(0,0,0,0.4);
  font-style: italic;
}

/* Team Section */
.team-section { padding: 0 0 60px; }
.section-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 2px 10px rgba(0,0,0,0.5);
  text-align: center;
  margin-bottom: 32px;
}

/* Team Card */
.team-card {
  background: rgba(255,255,255,0.35);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 16px;
  padding: 28px 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.15);
}
.dark-mode .team-card {
  background: rgba(30,21,53,0.45);
  border-color: rgba(124,92,255,0.3);
}
.founder-card {
  border-color: rgba(212,175,55,0.5);
  box-shadow: 0 4px 28px rgba(212,175,55,0.15);
}

/* Avatar */
.member-avatar {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 3px solid rgba(255,255,255,0.4);
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
}
.member-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}
.founder-avatar {
  width: 104px;
  height: 104px;
  border: 3px solid rgba(212,175,55,0.7);
  box-shadow: 0 4px 20px rgba(212,175,55,0.3);
}

/* Member info */
.member-info { flex: 1; }
.member-badge {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  border-radius: 20px;
  padding: 3px 12px;
  margin-bottom: 8px;
}
.founder-badge { background: rgba(212,175,55,0.25); color: #d4af37; border: 1px solid rgba(212,175,55,0.5); }
.co-founder-badge { background: rgba(99,102,241,0.2); color: #818cf8; border: 1px solid rgba(99,102,241,0.4); }

.member-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
  text-shadow: 0 1px 4px rgba(255,255,255,0.4);
}
.dark-mode .member-name { color: #f1f5f9; text-shadow: none; }

.member-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 12px;
}
.dark-mode .member-title { color: #94a3b8; }

.member-bio {
  font-size: 0.9rem;
  color: #1e293b;
  line-height: 1.7;
  margin-bottom: 10px;
}
.dark-mode .member-bio { color: #cbd5e1; }

.member-quote {
  font-style: italic;
  color: #d4af37;
  font-size: 0.9rem;
  font-weight: 600;
  border-left: 3px solid #d4af37;
  padding-left: 12px;
  margin-top: 12px;
}

/* CTA */
.about-cta {
  text-align: center;
  padding: 48px 0 0;
}
.cta-text {
  font-size: 1.3rem;
  color: #fff;
  font-weight: 600;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);
  margin-bottom: 16px;
}
.cta-btn {
  background: linear-gradient(135deg, #b8860b, #d4af37) !important;
  color: #fff !important;
  font-weight: 700 !important;
  border-radius: 10px !important;
  padding: 0 36px !important;
  box-shadow: 0 4px 16px rgba(212,175,55,0.35) !important;
}
</style>
